import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { sanitizeHtml } from '../../utils/Tools';
import './ProjectReviewPage.css';

interface IProjectReviewData {
  FormType: 'MediaRelease' | 'HoldHarmless' | string;
  FairName: string;
  StudentFullName: string;
  BodyHtml: string;
  Header: string;
  Label: string;
  Question: string;
  PermissionLabel: string;
  Signed: boolean;
  Approved?: boolean | null;
  Signature?: string | null;
  SignatureDisplay?: string | null;
  SignerFirstName?: string | null;
  SignerLastName?: string | null;
  SignerEmail?: string | null;
  RequireEmailConfirmation?: boolean;
  SignatureRequest: string;
}

const sameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const ProjectReviewPage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const signatureRequest = query.get('SignatureRequest') || '';
  const mediaRelease = query.get('MediaRelease') === 'Y' || query.get('MediaRelease') === '1' || query.get('MediaRelease')?.toLowerCase() === 'true';
  const holdHarmless = query.get('HoldHarmless') === 'Y' || query.get('HoldHarmless') === '1' || query.get('HoldHarmless')?.toLowerCase() === 'true';

  const [data, setData] = useState<IProjectReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState('true');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [confirmingCode, setConfirmingCode] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);

  const applySigner = (value: IProjectReviewData) => {
    setData(value);
    setError(null);
    if (value.Approved != null) setApproved(value.Approved ? 'true' : 'false');
    if (value.Signed) {
      setFirstName(value.SignerFirstName || '');
      setLastName(value.SignerLastName || '');
      setEmail(value.SignerEmail || '');
      setSignature(value.Signature || '');
    }
  };

  const load = () => {
    if (!signatureRequest) {
      setError('Missing signature request token.');
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    params.set('signatureRequest', signatureRequest);
    if (mediaRelease) params.set('mediaRelease', 'true');
    if (holdHarmless) params.set('holdHarmless', 'true');

    store.server
      .postApi<ServerResponse<IProjectReviewData>>(`../ProjectReview/Data?${params.toString()}`, {})
      .then((x) => {
        if (x.Success) {
          applySigner(x.Value);
        } else {
          setError(x.Message || 'Unable to load signature request.');
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Unable to load signature request.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureRequest]);

  const signatureTemplate = `${firstName.trim()} ${lastName.trim()}`.trim();
  const emailConfirmed = !!data?.RequireEmailConfirmation && confirmedEmail != null && sameEmail(email, confirmedEmail);

  const formFlags = () => ({
    MediaRelease: data?.FormType === 'MediaRelease',
    HoldHarmless: data?.FormType === 'HoldHarmless',
  });

  const sendCode = () => {
    if (!data) return;
    if (!email.trim()) {
      toast.warning('Enter the email address to confirm.');
      return;
    }
    setSendingCode(true);
    store.server
      .postApi<ServerResponse<{ Sent: boolean }>>('../ProjectReview/SendEmailCode', {
        SignatureRequest: data.SignatureRequest || signatureRequest,
        Email: email.trim(),
        ...formFlags(),
      })
      .then((x) => {
        if (x.Success) {
          setCodeSent(true);
          setCode('');
          setConfirmedEmail(null);
          toast.success('Confirmation code sent.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setSendingCode(false));
  };

  const confirmCode = () => {
    if (!data) return;
    if (!code.trim()) {
      toast.warning('Enter the code from the email.');
      return;
    }
    setConfirmingCode(true);
    store.server
      .postApi<ServerResponse<{ Confirmed: boolean }>>('../ProjectReview/ConfirmEmailCode', {
        SignatureRequest: data.SignatureRequest || signatureRequest,
        Email: email.trim(),
        Code: code.trim(),
        ...formFlags(),
      })
      .then((x) => {
        if (x.Success) {
          setConfirmedEmail(email.trim());
          toast.success('Email confirmed.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setConfirmingCode(false));
  };

  const sign = () => {
    if (!data) return;
    if (!firstName.trim() || !lastName.trim()) {
      toast.warning('First and last name are required.');
      return;
    }
    if (!email.trim()) {
      toast.warning('Email is required.');
      return;
    }
    if (!signature.trim()) {
      toast.warning('Signature is required.');
      return;
    }
    if (signature.trim().toLowerCase() !== signatureTemplate.toLowerCase()) {
      toast.error('Signature does not match. Type your name exactly as shown.', { autoClose: false });
      return;
    }
    if (data.RequireEmailConfirmation && !emailConfirmed) {
      toast.warning('Confirm the email address before signing.');
      return;
    }

    setSaving(true);
    store.server
      .postApi<ServerResponse<IProjectReviewData>>('../ProjectReview/Sign', {
        SignatureRequest: data.SignatureRequest || signatureRequest,
        Signature: signature.trim(),
        FirstName: firstName.trim(),
        LastName: lastName.trim(),
        Email: email.trim(),
        Approved: approved === 'true',
        ...formFlags(),
      })
      .then((x) => {
        if (x.Success) {
          applySigner(x.Value);
          toast.success('Signed successfully.');
        } else {
          toast.error(x.Message, { autoClose: false });
          if (x.Message && x.Message.indexOf('Confirm the email') >= 0) {
            setConfirmedEmail(null);
          }
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="main-body-page grow" data-testid="project-review-page">
        <h2>Project Review</h2>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-body-page grow" data-testid="project-review-page">
        <div className="alert alert-danger" data-testid="project-review-error">
          <h3>
            <i className="fa fa-exclamation-triangle" /> Expired Request
          </h3>
          <hr />
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const locked = data.Signed;

  return (
    <div className="main-body-page grow" data-testid="project-review-page">
      <h2
        data-testid="project-review-header"
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(data.Header || (data.FormType === 'HoldHarmless' ? 'Hold Harmless' : 'Media Release')),
        }}
      />
      <h3>
        {data.Label ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.Label) }} /> : null}
        {data.Label ? ' ' : null}
        <span className="project-review-student" data-testid="project-review-student">
          {data.StudentFullName}
        </span>
      </h3>
      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.BodyHtml || '') }} />
      {data.Question ? (
        <div className="head-room" data-testid="project-review-question" dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.Question) }} />
      ) : null}

      <div className="head-room" data-testid="signer-info">
        <h3 data-testid="signer-info-header">Signers Information - Parent / Guardian</h3>
        {!locked && (
          <p data-testid="signer-instructions">
            {data.RequireEmailConfirmation
              ? 'Enter your first name, last name, and email. Send a code to that email and enter it here before you sign. Then type your full name as your signature.'
              : 'Enter your first name, last name, and email. Then type your full name as your signature.'}
          </p>
        )}
      </div>

      <div className="form-horizontal head-room">
        <div className="form-group">
          <label className="col-sm-4 control-label">First name</label>
          <div className="col-sm-5">
            <input
              className="form-control"
              data-testid="signer-first"
              value={firstName}
              readOnly={locked}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-4 control-label">Last name</label>
          <div className="col-sm-5">
            <input
              className="form-control"
              data-testid="signer-last"
              value={lastName}
              readOnly={locked}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-4 control-label">Email</label>
          <div className="col-sm-5">
            <input
              className="form-control"
              data-testid="signer-email"
              value={email}
              readOnly={locked}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        {!locked && data.RequireEmailConfirmation && (
          <div className="form-group" data-testid="email-code-panel">
            <div className="col-sm-5 col-sm-offset-4">
              <button
                type="button"
                className="btn btn-default"
                data-testid="send-email-code"
                disabled={sendingCode}
                onClick={sendCode}
              >
                {sendingCode ? 'Sending...' : codeSent ? 'Resend code' : 'Send code'}
              </button>
              {codeSent && !emailConfirmed && (
                <div className="head-room">
                  <p>We sent a code to {email.trim()}. Enter it here, then sign. The code expires in 15 minutes.</p>
                  <input
                    className="form-control"
                    data-testid="email-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    className="btn btn-default head-room"
                    data-testid="confirm-email-code"
                    disabled={confirmingCode}
                    onClick={confirmCode}
                  >
                    {confirmingCode ? 'Checking...' : 'Confirm email'}
                  </button>
                </div>
              )}
              {emailConfirmed && (
                <div className="text-success head-room" data-testid="email-confirmed">
                  Email confirmed.
                </div>
              )}
            </div>
          </div>
        )}

        {data.FormType === 'MediaRelease' && (
          <div className="form-group">
            <label className="col-sm-4 control-label">
              {data.PermissionLabel ? (
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.PermissionLabel) }} />
              ) : (
                'Permission'
              )}
            </label>
            <div className="col-sm-5">
              <select
                className="form-control"
                data-testid="media-release-permission"
                disabled={locked}
                value={approved}
                onChange={(e) => setApproved(e.target.value)}
              >
                <option value="true">YES, I give my permission</option>
                <option value="false">NO, I do not give permission</option>
              </select>
            </div>
          </div>
        )}
        <div className="form-group">
          <label className="col-sm-4 control-label">Signature:</label>
          <div className="col-sm-5">
            {!locked && signatureTemplate && (
              <div>
                Type your signature as follows: <strong data-testid="signature-template">{signatureTemplate}</strong>
              </div>
            )}
            <input
              className="form-control"
              data-testid="signature-input"
              value={signature}
              readOnly={locked}
              onChange={(e) => setSignature(e.target.value)}
            />
          </div>
        </div>
        {!locked && (
          <div className="form-group">
            <div className="col-sm-5 col-sm-offset-4">
              <button
                type="button"
                className="btn btn-success"
                data-testid="sign-button"
                disabled={saving || (!!data.RequireEmailConfirmation && !emailConfirmed)}
                onClick={sign}
              >
                {saving ? 'Signing...' : 'Sign'}
              </button>
            </div>
          </div>
        )}
        {locked && (
          <div className="alert alert-success" data-testid="already-signed">
            Already signed.
            {data.SignatureDisplay ? <div>{data.SignatureDisplay}</div> : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectReviewPage;
